import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { CornerDownLeft, Search, X } from "lucide-react";

/**
 * Request 1236 — find a menu in the sidebar. A small magnifier sits in the sidebar's top bar; click it (or press "/") and a slim
 * search box rolls down under the logo. As you type, the menu below gives way to the matches — every module, sub-menu, Tools and
 * Settings, only the ones this person can open — best match first, the typed letters lit up, each with where it lives
 * ("HRMS › Payroll"). ↑ ↓ to move, Enter to open, Esc to close. Matching is forgiving: "lv rq" finds Leave Request, "pyrl"
 * finds Payroll.
 */
export type MenuEntry = { to: string; label: string; icon: React.ElementType; visible: boolean; locked?: boolean; children?: MenuEntry[]; pageTo?: string; /** not a page — e.g. Sticky Notes opens its layer */ onSelect?: () => void };
export type MenuGroup = { title: string; items: MenuEntry[] };
type Hit = { to: string; label: string; icon: React.ElementType; path: string; marks: number[]; score: number; onSelect?: () => void };

/** Every page in the menu this person can open, with where it sits. */
function flatten(groups: MenuGroup[]) {
  const out: Array<{ to: string; label: string; icon: React.ElementType; path: string; onSelect?: () => void }> = [];
  const walk = (items: MenuEntry[], path: string) => {
    for (const it of items) {
      if (!it.visible || it.locked) continue;
      const kids = (it.children ?? []).filter((c) => c.visible);
      if (kids.length) {
        if (it.pageTo) out.push({ to: it.pageTo, label: it.label, icon: it.icon, path });
        walk(kids, `${path} › ${it.label}`);
      } else out.push({ to: it.to, label: it.label, icon: it.icon, path, onSelect: it.onSelect });
    }
  };
  for (const g of groups) walk(g.items, g.title);
  const seen = new Set<string>();
  return out.filter((e) => (seen.has(e.to + e.label) ? false : (seen.add(e.to + e.label), true)));
}

/** Scores how well `q` matches `text`: a whole-word start beats a word start beats letters in order. Returns the lit letters. */
function match(text: string, q: string): { score: number; marks: number[] } | null {
  const t = text.toLowerCase();
  const query = q.toLowerCase().trim();
  if (!query) return null;
  const at = t.indexOf(query);
  if (at >= 0) {
    const wordStart = at === 0 || /\W/.test(t[at - 1]);
    return { score: (at === 0 ? 100 : wordStart ? 80 : 60) - t.length * 0.1, marks: Array.from({ length: query.length }, (_, i) => at + i) };
  }
  // letters in order, preferring the starts of words ("lv rq" → Leave Request)
  const marks: number[] = [];
  let ti = 0;
  let bonus = 0;
  for (const ch of query.replace(/\s+/g, "")) {
    while (ti < t.length && t[ti] !== ch) ti++;
    if (ti >= t.length) return null;
    if (ti === 0 || /\W/.test(t[ti - 1])) bonus += 3;
    marks.push(ti++);
  }
  const spread = marks[marks.length - 1] - marks[0];
  return { score: 30 + bonus - spread * 0.5 - t.length * 0.1, marks };
}

function Lit({ text, marks }: { text: string; marks: number[] }) {
  const set = new Set(marks);
  return (
    <>
      {[...text].map((ch, i) =>
        set.has(i) ? (
          <mark key={i} className="ms-lit rounded-sm bg-transparent font-bold text-white" style={{ animationDelay: `${i * 18}ms` }}>
            {ch}
          </mark>
        ) : (
          <React.Fragment key={i}>{ch}</React.Fragment>
        )
      )}
    </>
  );
}

/** The magnifier in the top bar. */
/** request 1242: `inline` sits it at the end of the Dashboard row instead of floating by the logo */
export function MenuSearchButton({ open, onToggle, inline = false }: { open: boolean; onToggle: () => void; inline?: boolean }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={clsx(
        "ms-btn flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-white/10 hover:text-white",
        inline ? "relative shrink-0" : "absolute left-2 top-1/2 -translate-y-1/2",
        open && "is-open bg-white/10 text-white"
      )}
      aria-label={open ? "Close menu search" : "Search the menu"}
      aria-expanded={open}
      title={open ? "Close (Esc)" : "Search the menu ( / )"}
    >
      <Search className="ms-btn-search h-4 w-4" />
      <X className="ms-btn-x absolute h-4 w-4" />
    </button>
  );
}

/** The search box (under the logo) and, while there's a query, the matches in place of the menu. */
export function MenuSearchPanel({ open, onClose, groups, onNavigate, children }: { open: boolean; onClose: () => void; groups: MenuGroup[]; onNavigate?: () => void; children: React.ReactNode }) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const entries = useMemo(() => flatten(groups), [groups]);

  const hits: Hit[] = useMemo(() => {
    if (!q.trim()) return [];
    return entries
      .map((e) => {
        const m = match(e.label, q);
        const inPath = !m && match(e.path, q);
        return m ? { ...e, ...m } : inPath ? { ...e, score: inPath.score - 40, marks: [] } : null;
      })
      .filter((h): h is Hit => !!h)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12);
  }, [entries, q]);

  useEffect(() => {
    if (open) window.setTimeout(() => inputRef.current?.focus(), 120);
    else setQ("");
  }, [open]);
  useEffect(() => setSel(0), [q]);
  // nothing matches: a small shake of the box (restarted in place, so the input keeps focus)
  useEffect(() => {
    const el = boxRef.current;
    if (!el || !q.trim() || hits.length) return;
    el.classList.remove("ms-shake");
    void el.offsetWidth;
    el.classList.add("ms-shake");
  }, [q, hits.length]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-i="${sel}"]`)?.scrollIntoView({ block: "nearest" });
  }, [sel]);

  const go = (h: Hit) => {
    if (h.onSelect) h.onSelect();
    else navigate(h.to);
    onClose();
    onNavigate?.();
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (q) setQ("");
      else onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSel((s) => Math.min(hits.length - 1, s + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSel((s) => Math.max(0, s - 1));
    } else if (e.key === "Enter" && hits[sel]) {
      e.preventDefault();
      go(hits[sel]);
    }
  };

  const searching = open && !!q.trim();
  return (
    <>
      <style>{MS_CSS}</style>
      {/* the box rolls down under the logo */}
      <div className={clsx("grid shrink-0 px-3 transition-[grid-template-rows,opacity] duration-300 ease-out", open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0")}>
        <div className="min-h-0 overflow-hidden">
          <div ref={boxRef} className="ms-box relative mb-1 mt-1 flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-2.5 py-1.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKey}
              tabIndex={open ? 0 : -1}
              placeholder="Find a menu…"
              className="min-w-0 flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
              aria-label="Find a menu"
            />
            {q ? (
              <button type="button" onClick={() => (setQ(""), inputRef.current?.focus())} className="text-slate-500 hover:text-white" aria-label="Clear">
                <X className="h-3.5 w-3.5" />
              </button>
            ) : (
              <kbd className="rounded border border-white/15 px-1 font-mono text-[10px] text-slate-500">/</kbd>
            )}
            <span className="ms-line pointer-events-none absolute inset-x-3 -bottom-px h-px" />
          </div>
        </div>
      </div>

      {/* the menu, or the matches in its place */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div className={clsx("flex min-h-0 flex-1 flex-col transition duration-200", searching && "pointer-events-none scale-[0.98] opacity-0 blur-[2px]")} aria-hidden={searching}>
          {children}
        </div>
        {searching && (
          <div className="absolute inset-0 overflow-y-auto px-3 py-3">
            <p className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{hits.length ? `${hits.length} match${hits.length === 1 ? "" : "es"}` : "No match"}</p>
            {hits.length ? (
              <ul ref={listRef} className="space-y-0.5" role="listbox">
                {hits.map((h, i) => {
                  const Icon = h.icon;
                  return (
                    <li key={h.to + h.label} data-i={i} className="ms-in" style={{ animationDelay: `${i * 35}ms` }}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={i === sel}
                        onMouseEnter={() => setSel(i)}
                        onClick={() => go(h)}
                        className={clsx("relative flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors", i === sel ? "bg-brand-600 text-white shadow-sm" : "text-slate-300 hover:bg-white/5")}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">
                            <Lit text={h.label} marks={h.marks} />
                          </span>
                          <span className={clsx("block truncate text-[10px]", i === sel ? "text-white/70" : "text-slate-500")}>{h.path}</span>
                        </span>
                        {i === sel && <CornerDownLeft className="ms-enter h-3.5 w-3.5 shrink-0 text-white/80" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="ms-in px-1 py-6 text-center">
                <Search className="mx-auto mb-2 h-6 w-6 text-slate-600" />
                <p className="text-xs text-slate-400">No menu called “{q.trim()}”.</p>
                <p className="mt-1 text-[11px] text-slate-500">Try part of the name, like “leave” or “pay”.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

const MS_CSS = `
.ms-btn .ms-btn-search, .ms-btn .ms-btn-x { transition: transform .35s cubic-bezier(.2,.8,.2,1), opacity .2s ease; }
.ms-btn .ms-btn-x { opacity: 0; transform: rotate(-90deg) scale(.5); }
.ms-btn.is-open .ms-btn-search { opacity: 0; transform: rotate(90deg) scale(.5); }
.ms-btn.is-open .ms-btn-x { opacity: 1; transform: none; }
.ms-btn:not(.is-open):hover .ms-btn-search { animation: ms-peek 1s ease-in-out; }
.ms-line { background: linear-gradient(90deg, transparent, #818cf8, #e879f9, transparent); transform: scaleX(0); transition: transform .4s ease; }
.ms-box:focus-within { border-color: rgba(129,140,248,.5); background: rgba(255,255,255,.09); }
.ms-box:focus-within .ms-line { transform: scaleX(1); }
.ms-in { opacity: 0; animation: ms-in .3s cubic-bezier(.2,.8,.2,1) forwards; }
.ms-lit { color: #c7d2fe; text-shadow: 0 0 8px rgba(129,140,248,.8); animation: ms-glow .5s ease both; }
[aria-selected="true"] .ms-lit { color: #fff; text-shadow: 0 0 6px rgba(255,255,255,.6); }
.ms-enter { animation: ms-in .25s ease both; }
.ms-shake { animation: ms-shake .35s ease; }
@keyframes ms-in { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: none; } }
@keyframes ms-glow { from { text-shadow: 0 0 0 transparent; } }
@keyframes ms-peek { 0%,100% { transform: none; } 30% { transform: translate(-1px,-1px) rotate(-12deg) scale(1.15); } 60% { transform: translate(1px,0) rotate(8deg) scale(1.1); } }
@keyframes ms-shake { 0%,100% { transform: none; } 25% { transform: translateX(-4px); } 50% { transform: translateX(4px); } 75% { transform: translateX(-2px); } }
@media (prefers-reduced-motion: reduce) { .ms-in, .ms-lit, .ms-enter, .ms-shake, .ms-btn:hover .ms-btn-search { animation: none !important; opacity: 1; } }
`;
